import React, { ReactNode, useContext } from 'react';
import styled from 'styled-components';

import { FatalAttraction } from '@vpr/tracking';

import { rem, Text as BokehText } from '@vimeo/bokeh';
import { bokehTheme as theme } from '@vimeo/bokeh-theme';
import { translate } from '@vimeo/i18n-runtime';
import { Text } from '@vimeo/iris/typography';
import { ViewerContext } from '@vimeo/viewer';

import Logo from '../../Logo';
import DoNotSellLink from '../DoNotSellLink';
import {
  getSiteMapLinks,
  getAbsoluteUrl,
  getLegalLinks,
  LegalLink as LegalLinkType,
} from '../Links';

interface Props {
  absoluteUrl?: boolean;
  languageSelect: ReactNode;
  format?: 'alternative';
  impressumQualifies?: boolean;
  isDoNotSellReady?: boolean;
}

export default function StandardFooter({
  absoluteUrl = false,
  languageSelect,
  format = 'alternative',
  impressumQualifies,
  isDoNotSellReady,
}: Props) {
  const viewer = useContext(ViewerContext);
  const terminateContractQualifies = viewer?.terminateContractQualifies;
  const [terms, privacy, caPrivacy, impressum, copyright, cookies] = getLegalLinks();

  const siteMapLinks = getSiteMapLinks(!!terminateContractQualifies);

  return (
    <Footer>
      <SectionStyled key={`lovely-footer-00`}>
        <a
          href="/"
          aria-label={translate('Vimeo logo. Click on this link to navigate to the homepage.')}
        >
          <Logo color={theme.colors.white as string} width={rem(120)} />
        </a>
      </SectionStyled>
      {siteMapLinks.map(({ header, links }, i) => (
        <SectionStyled key={`${header}-section-${i}`}>
          <BokehText size="6" style={{ color: theme.colors.gray['200'] }}>
            {header}
          </BokehText>
          {links.map(
            (link, index) =>
              link && (
                <LinkedFooterItem
                  href={absoluteUrl ? getAbsoluteUrl(link.href) : link.href}
                  key={`${header}-link-${index}`}
                  onClick={() => link.fa && FatalAttraction.trackClick(link.fa)}
                >
                  {link.label}
                </LinkedFooterItem>
              ),
          )}
        </SectionStyled>
      ))}
      <Auxiliary key={`lovely-footer-01`}>
        <LegalText>
          {translate({
            singular: '© {YEAR} Vimeo.com, Inc. All rights reserved.',
            replacements: {
              YEAR: new Date().getFullYear().toString(),
            },
          })}{' '}
          <LegalLink linkObj={terms} absoluteUrl={absoluteUrl} />
          {' | '}
          <LegalLink linkObj={privacy} absoluteUrl={absoluteUrl} />
          {!impressumQualifies && <DoNotSellLink isDoNotSellReady={isDoNotSellReady} />}
          {' | '}
          <LegalLink linkObj={caPrivacy} absoluteUrl={absoluteUrl} />
          {' | '}
          {impressumQualifies && (
            <>
              <LegalLink linkObj={impressum} absoluteUrl={absoluteUrl} /> {' | '}
            </>
          )}
          <LegalLink linkObj={copyright} absoluteUrl={absoluteUrl} />
          {' | '}
          <LegalLink linkObj={cookies} absoluteUrl={absoluteUrl} />
        </LegalText>
        <AuxText key={`iStillLoveFooter-01`} format={format}>
          {translate('Language')}
        </AuxText>
        <SelectWrapper key={`iStillLoveFooter-10`}>{languageSelect}</SelectWrapper>
      </Auxiliary>
    </Footer>
  );
}

const LegalLink = ({ linkObj, absoluteUrl }: { absoluteUrl: boolean; linkObj: LegalLinkType }) => (
  <InlineLink href={absoluteUrl ? getAbsoluteUrl(linkObj.href) : linkObj.href}>
    {linkObj.label}
  </InlineLink>
);

const Footer = styled.footer`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: auto;
  padding: 4rem 2.75rem 2rem 2.67rem;
  background: ${theme.colors.gray['800']};

  @media (max-width: 62.5rem) {
    display: none;
  }
`;

const SectionStyled = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 11.25rem;
  & + & {
    padding-left: 20px;
  }
  a {
    color: ${theme.colors.white};
  }
`;

const LegalText = styled(Text)`
  margin-right: auto;
  color: ${theme.colors.white};
  cursor: default;
`;

const AuxText = styled(Text)`
  margin-left: 1rem;
  color: ${theme.colors.white};
`;

const SelectWrapper = styled.div`
  margin-left: 1rem;
`;

const Auxiliary = styled.div`
  grid-column: 1/6;
  display: flex;
  width: 100%;
  margin-top: 2rem;
  align-items: center;
  justify-content: flex-end;
`;

const LinkedFooterItem = styled(Text).attrs({ element: 'a' })`
  display: block;
  margin-bottom: 0.5rem;
  color: ${theme.colors.white};
  text-decoration: none;

  &:hover {
    text-decoration: none;
    color: ${theme.colors.gray['500']};
  }
`;

export const InlineLink = styled(Text).attrs({ element: 'a' })`
  display: inline;
  margin-bottom: 0.5rem;
  color: ${theme.colors.white};
  text-decoration: none;

  &:hover {
    text-decoration: none;
    color: ${theme.colors.gray['500']};
  }
`;
